/**
 * MergeFieldCatalog — Tier-3 Disclosure Engine M4.
 *
 * Two stacked CRUD tables:
 *   - MergeFieldGroups       (Code, Name, Description, SortOrder, Active)
 *   - MergeFieldDefinitions  (Code, Label, Group, DataSource, DataPath,
 *                             Formatter, IsRequired, Active)
 *
 * Wires endpoints:
 *   POST   /v3/admin/merge-field-groups
 *   DELETE /v3/admin/merge-field-groups/{code}
 *   POST   /v3/admin/merge-field-definitions
 *   DELETE /v3/admin/merge-field-definitions/{code}
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Plus, RefreshCw, Search, Trash2, Edit3 } from 'lucide-react';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import type {
  MergeFieldDefinitionRow,
  MergeFieldGroupRow,
} from '@/services/disclosuresApi';
import {
  upsertGroup,
  deleteGroup,
  upsertDefinition,
  deleteDefinition,
  type MergeFieldGroupUpsertInput,
  type MergeFieldDefinitionUpsertInput,
} from '@/services/disclosuresAdminApi';

import {
  Checkbox,
  ConfirmDialog,
  Field,
  FieldGrid,
  Modal,
  NumberInput,
  Select,
  TextArea,
  TextInput,
  Toast,
  useToast,
} from './_shared';

const DATA_SOURCE_OPTIONS = [
  '',
  'cart',
  'cif',
  'product',
  'branch',
  'officer',
  'config',
  'system',
  'riskMgmt',
  'sigCard',
  'iraPlan',
];

const FORMATTER_OPTIONS = [
  '',
  'text',
  'date',
  'ssn',
  'ein',
  'phone',
  'currency',
  'percent',
  'boolean',
  'raw',
];

// ─── Group form ──────────────────────────────────────────────────────────

interface GroupForm {
  code: string;
  name: string;
  description: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_GROUP: GroupForm = {
  code: '',
  name: '',
  description: '',
  sortOrder: '0',
  isActive: true,
};

function groupRowToForm(r: MergeFieldGroupRow | null): GroupForm {
  if (!r) return EMPTY_GROUP;
  const get = (k: string): string => {
    const v = (r as Record<string, unknown>)[k];
    return v === null || v === undefined ? '' : String(v);
  };
  return {
    code: get('groupCode') || get('code'),
    name: get('displayName') || get('name'),
    description: get('description'),
    sortOrder: get('sortOrder') || '0',
    isActive: ((r as Record<string, unknown>)['isActive'] as boolean | undefined) !== false,
  };
}

function groupFormToInput(f: GroupForm): MergeFieldGroupUpsertInput {
  return {
    code: f.code.trim(),
    name: f.name.trim() || null,
    description: f.description.trim() || null,
    sortOrder: f.sortOrder.trim() ? Number(f.sortOrder) : 0,
    isActive: f.isActive,
  };
}

// ─── Definition form ─────────────────────────────────────────────────────

interface DefForm {
  code: string;
  label: string;
  groupCode: string;
  dataSource: string;
  dataPath: string;
  formatter: string;
  isRequired: boolean;
  isActive: boolean;
}

const EMPTY_DEF: DefForm = {
  code: '',
  label: '',
  groupCode: '',
  dataSource: '',
  dataPath: '',
  formatter: 'text',
  isRequired: false,
  isActive: true,
};

function defRowToForm(r: MergeFieldDefinitionRow | null): DefForm {
  if (!r) return EMPTY_DEF;
  const get = (k: string): string => {
    const v = (r as Record<string, unknown>)[k];
    return v === null || v === undefined ? '' : String(v);
  };
  return {
    code: get('fieldCode') || get('code'),
    label: get('displayName') || get('label'),
    groupCode: get('groupCode'),
    dataSource: get('dataSource'),
    dataPath: get('dataPath'),
    formatter: get('formatter') || 'text',
    isRequired:
      ((r as Record<string, unknown>)['isRequired'] as boolean | undefined) ?? false,
    isActive:
      ((r as Record<string, unknown>)['isActive'] as boolean | undefined) !== false,
  };
}

function defFormToInput(f: DefForm): MergeFieldDefinitionUpsertInput {
  return {
    code: f.code.trim(),
    label: f.label.trim() || null,
    groupCode: f.groupCode.trim() || null,
    dataSource: f.dataSource.trim() || null,
    dataPath: f.dataPath.trim() || null,
    formatter: f.formatter.trim() || null,
    isRequired: f.isRequired,
    isActive: f.isActive,
  };
}

// ─── Component ───────────────────────────────────────────────────────────

export function MergeFieldCatalog(): JSX.Element {
  const { envelope, state, error, refresh } = useDisclosuresConfig();
  const [defFilter, setDefFilter] = useState<string>('');
  const { toast, push, clear } = useToast();

  // Groups state
  const [groupOpen, setGroupOpen] = useState<boolean>(false);
  const [groupEditing, setGroupEditing] = useState<MergeFieldGroupRow | null>(null);
  const [groupForm, setGroupForm] = useState<GroupForm>(EMPTY_GROUP);
  const [groupBusy, setGroupBusy] = useState<boolean>(false);
  const [groupErr, setGroupErr] = useState<string | null>(null);
  const [confirmGroupDelete, setConfirmGroupDelete] = useState<MergeFieldGroupRow | null>(null);

  // Definitions state
  const [defOpen, setDefOpen] = useState<boolean>(false);
  const [defEditing, setDefEditing] = useState<MergeFieldDefinitionRow | null>(null);
  const [defForm, setDefForm] = useState<DefForm>(EMPTY_DEF);
  const [defBusy, setDefBusy] = useState<boolean>(false);
  const [defErr, setDefErr] = useState<string | null>(null);
  const [confirmDefDelete, setConfirmDefDelete] =
    useState<MergeFieldDefinitionRow | null>(null);

  useEffect(() => {
    if (groupOpen) {
      setGroupForm(groupRowToForm(groupEditing));
      setGroupErr(null);
    }
  }, [groupOpen, groupEditing]);

  useEffect(() => {
    if (defOpen) {
      setDefForm(defRowToForm(defEditing));
      setDefErr(null);
    }
  }, [defOpen, defEditing]);

  const groupCodes = useMemo<string[]>(() => {
    const s = new Set<string>();
    for (const g of envelope.mergeFieldGroups) {
      const c = String(g.groupCode ?? '');
      if (c) s.add(c);
    }
    return Array.from(s).sort();
  }, [envelope.mergeFieldGroups]);

  const filteredDefs = useMemo<ReadonlyArray<MergeFieldDefinitionRow>>(() => {
    const q = defFilter.trim().toLowerCase();
    if (!q) return envelope.mergeFieldDefinitions;
    return envelope.mergeFieldDefinitions.filter((d) => {
      const haystack = [d.fieldCode, d.groupCode, d.displayName, d.formatter]
        .map((x) => String(x ?? '').toLowerCase())
        .join('|');
      return haystack.includes(q);
    });
  }, [envelope.mergeFieldDefinitions, defFilter]);

  const setGroup = <K extends keyof GroupForm>(key: K, value: GroupForm[K]) =>
    setGroupForm((p) => ({ ...p, [key]: value }));
  const setDef = <K extends keyof DefForm>(key: K, value: DefForm[K]) =>
    setDefForm((p) => ({ ...p, [key]: value }));

  const submitGroup = async (): Promise<void> => {
    if (groupBusy || !groupForm.code.trim()) return;
    setGroupBusy(true);
    setGroupErr(null);
    try {
      const result = await upsertGroup(groupFormToInput(groupForm));
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setGroupErr(msg);
        push('err', msg);
        return;
      }
      push('ok', `Group ${groupForm.code} saved.`);
      setGroupOpen(false);
      setGroupEditing(null);
      void refresh();
    } finally {
      setGroupBusy(false);
    }
  };

  const submitDef = async (): Promise<void> => {
    if (defBusy || !defForm.code.trim()) return;
    setDefBusy(true);
    setDefErr(null);
    try {
      const result = await upsertDefinition(defFormToInput(defForm));
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setDefErr(msg);
        push('err', msg);
        return;
      }
      push('ok', `Definition ${defForm.code} saved.`);
      setDefOpen(false);
      setDefEditing(null);
      void refresh();
    } finally {
      setDefBusy(false);
    }
  };

  const onDeleteGroup = useCallback(async (): Promise<void> => {
    if (!confirmGroupDelete) return;
    const c = String(confirmGroupDelete.groupCode ?? '');
    if (!c) {
      setConfirmGroupDelete(null);
      return;
    }
    setGroupBusy(true);
    try {
      const result = await deleteGroup(c);
      if (!result.success) {
        push('err', result.error ?? 'Delete failed.');
        return;
      }
      push('ok', `Group ${c} deleted.`);
      setConfirmGroupDelete(null);
      void refresh();
    } finally {
      setGroupBusy(false);
    }
  }, [confirmGroupDelete, push, refresh]);

  const onDeleteDef = useCallback(async (): Promise<void> => {
    if (!confirmDefDelete) return;
    const c = String(confirmDefDelete.fieldCode ?? '');
    if (!c) {
      setConfirmDefDelete(null);
      return;
    }
    setDefBusy(true);
    try {
      const result = await deleteDefinition(c);
      if (!result.success) {
        push('err', result.error ?? 'Delete failed.');
        return;
      }
      push('ok', `Definition ${c} deleted.`);
      setConfirmDefDelete(null);
      void refresh();
    } finally {
      setDefBusy(false);
    }
  }, [confirmDefDelete, push, refresh]);

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures" className="back-link">Disclosure Engine</Link>
        <span aria-hidden="true"> / </span>
        <span>Merge field catalog</span>
      </nav>

      <Link to="/disclosures" className="back-link">
        <ArrowLeft size={14} /> Back to Disclosure Engine
      </Link>

      <section className="page-header">
        <h1 className="page-header__title">Merge Field Catalog</h1>
        <p className="page-header__subtitle page-intro">
          Source-of-truth vocabulary for <code>{`{{token}}`}</code>{' '}
          substitution at render time. Replaces SPFx{' '}
          <code>MergeFieldRegistry.ts</code> hardcoded list (Tier-3 decision #6).
        </p>
      </section>

      {error && <div className="banner banner--err" role="alert">{error}</div>}

      {/* ── Groups ── */}
      <section className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Field groups ({envelope.mergeFieldGroups.length})</h2>
          <div className="toolbar__spacer" style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void refresh()}
            disabled={state === 'loading'}
          >
            <RefreshCw size={14} /> {state === 'loading' ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setGroupEditing(null);
              setGroupOpen(true);
            }}
          >
            <Plus size={14} /> New group
          </button>
        </div>

        {envelope.mergeFieldGroups.length === 0 ? (
          <div className="empty">No groups loaded.</div>
        ) : (
          <div className="table-wrap">
            <table className="table table--striped table--hover">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Sort</th>
                  <th>Active</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {envelope.mergeFieldGroups.map((g, i) => (
                  <tr key={`${String(g.groupCode)}-${i}`}>
                    <td><code className="cell-id">{String(g.groupCode ?? '—')}</code></td>
                    <td>{g.displayName ? String(g.displayName) : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-600)' }}>
                      {String((g as Record<string, unknown>)['description'] ?? '—')}
                    </td>
                    <td>{String((g as Record<string, unknown>)['sortOrder'] ?? 0)}</td>
                    <td>
                      {(g as Record<string, unknown>)['isActive'] === false ? (
                        <span className="pill pill--muted">Inactive</span>
                      ) : (
                        <span className="pill pill--ok">Active</span>
                      )}
                    </td>
                    <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          setGroupEditing(g);
                          setGroupOpen(true);
                        }}
                      >
                        <Edit3 size={12} /> Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setConfirmGroupDelete(g)}
                        style={{ color: 'var(--pcb-red)' }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Definitions ── */}
      <section className="card card--inset">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Field definitions ({envelope.mergeFieldDefinitions.length})</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={14} aria-hidden="true" />
            <TextInput
              placeholder="Filter by code, group, formatter…"
              value={defFilter}
              onChange={(e) => setDefFilter(e.target.value)}
              style={{ minWidth: 240, width: 240 }}
            />
          </div>
          <div className="toolbar__spacer" style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setDefEditing(null);
              setDefOpen(true);
            }}
          >
            <Plus size={14} /> New definition
          </button>
        </div>

        {filteredDefs.length === 0 ? (
          <div className="empty">No definitions match the current filter.</div>
        ) : (
          <div className="table-wrap">
            <table className="table table--striped table--hover">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Label</th>
                  <th>Group</th>
                  <th>DataSource</th>
                  <th>DataPath</th>
                  <th>Formatter</th>
                  <th>Required</th>
                  <th>Active</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDefs.map((d, i) => (
                  <tr key={`${String(d.fieldCode)}-${i}`}>
                    <td><code className="cell-id">{String(d.fieldCode ?? '—')}</code></td>
                    <td>{d.displayName ? String(d.displayName) : '—'}</td>
                    <td>
                      {d.groupCode ? (
                        <span className="pill pill--info">{String(d.groupCode)}</span>
                      ) : '—'}
                    </td>
                    <td>{String((d as Record<string, unknown>)['dataSource'] ?? '—')}</td>
                    <td>
                      <code style={{ fontSize: 11 }}>
                        {String((d as Record<string, unknown>)['dataPath'] ?? '—')}
                      </code>
                    </td>
                    <td>{d.formatter ? <code>{String(d.formatter)}</code> : '—'}</td>
                    <td>
                      {(d as Record<string, unknown>)['isRequired'] === true ? (
                        <span className="pill pill--err">Required</span>
                      ) : (
                        <span className="pill pill--muted">Optional</span>
                      )}
                    </td>
                    <td>
                      {(d as Record<string, unknown>)['isActive'] === false ? (
                        <span className="pill pill--muted">Inactive</span>
                      ) : (
                        <span className="pill pill--ok">Active</span>
                      )}
                    </td>
                    <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          setDefEditing(d);
                          setDefOpen(true);
                        }}
                      >
                        <Edit3 size={12} /> Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setConfirmDefDelete(d)}
                        style={{ color: 'var(--pcb-red)' }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Group editor modal ── */}
      <Modal
        open={groupOpen}
        onClose={() => {
          if (!groupBusy) {
            setGroupOpen(false);
            setGroupEditing(null);
          }
        }}
        title={groupEditing ? `Edit group ${groupForm.code}` : 'New merge field group'}
        width={520}
        closeOnBackdrop={!groupBusy}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setGroupOpen(false);
                setGroupEditing(null);
              }}
              disabled={groupBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submitGroup()}
              disabled={groupBusy || !groupForm.code.trim()}
            >
              {groupBusy ? 'Saving…' : groupEditing ? 'Save' : 'Create'}
            </button>
          </>
        }
      >
        {groupErr && (
          <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
            {groupErr}
          </div>
        )}
        <FieldGrid columns={2}>
          <Field label="Code" required>
            <TextInput
              value={groupForm.code}
              onChange={(e) => setGroup('code', e.target.value)}
              readOnly={groupEditing !== null}
            />
          </Field>
          <Field label="Sort order">
            <NumberInput
              value={groupForm.sortOrder}
              onChange={(e) => setGroup('sortOrder', e.target.value)}
            />
          </Field>
          <Field label="Name" span={2}>
            <TextInput
              value={groupForm.name}
              onChange={(e) => setGroup('name', e.target.value)}
            />
          </Field>
          <Field label="Description" span={2}>
            <TextArea
              value={groupForm.description}
              onChange={(e) => setGroup('description', e.target.value)}
              rows={2}
            />
          </Field>
          <Field label="Flags" span={2}>
            <Checkbox
              label="Active"
              checked={groupForm.isActive}
              onChange={(e) => setGroup('isActive', e.target.checked)}
            />
          </Field>
        </FieldGrid>
      </Modal>

      {/* ── Definition editor modal ── */}
      <Modal
        open={defOpen}
        onClose={() => {
          if (!defBusy) {
            setDefOpen(false);
            setDefEditing(null);
          }
        }}
        title={defEditing ? `Edit definition ${defForm.code}` : 'New merge field definition'}
        width={620}
        closeOnBackdrop={!defBusy}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setDefOpen(false);
                setDefEditing(null);
              }}
              disabled={defBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submitDef()}
              disabled={defBusy || !defForm.code.trim()}
            >
              {defBusy ? 'Saving…' : defEditing ? 'Save' : 'Create'}
            </button>
          </>
        }
      >
        {defErr && (
          <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
            {defErr}
          </div>
        )}
        <FieldGrid columns={2}>
          <Field label="Code" required hint="e.g. customer.firstName">
            <TextInput
              value={defForm.code}
              onChange={(e) => setDef('code', e.target.value)}
              readOnly={defEditing !== null}
            />
          </Field>
          <Field label="Label" required>
            <TextInput
              value={defForm.label}
              onChange={(e) => setDef('label', e.target.value)}
            />
          </Field>
          <Field label="Group">
            <Select
              value={defForm.groupCode}
              onChange={(e) => setDef('groupCode', e.target.value)}
              aria-label="Group code"
              title="Group code"
            >
              <option value="">(no group)</option>
              {groupCodes.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Select>
          </Field>
          <Field label="Data source">
            <Select
              value={defForm.dataSource}
              onChange={(e) => setDef('dataSource', e.target.value)}
              aria-label="Data source"
              title="Data source"
            >
              {DATA_SOURCE_OPTIONS.map((o) => (
                <option key={o || '_none'} value={o}>{o || '(none)'}</option>
              ))}
            </Select>
          </Field>
          <Field label="Data path" hint="Dotted path into the resolved DataSource." span={2}>
            <TextInput
              value={defForm.dataPath}
              onChange={(e) => setDef('dataPath', e.target.value)}
              placeholder="e.g. cart.applicant.firstName"
            />
          </Field>
          <Field label="Formatter">
            <Select
              value={defForm.formatter}
              onChange={(e) => setDef('formatter', e.target.value)}
              aria-label="Formatter"
              title="Formatter"
            >
              {FORMATTER_OPTIONS.map((o) => (
                <option key={o || '_none'} value={o}>{o || '(none)'}</option>
              ))}
            </Select>
          </Field>
          <Field label="Flags">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Checkbox
                label="Required"
                checked={defForm.isRequired}
                onChange={(e) => setDef('isRequired', e.target.checked)}
              />
              <Checkbox
                label="Active"
                checked={defForm.isActive}
                onChange={(e) => setDef('isActive', e.target.checked)}
              />
            </div>
          </Field>
        </FieldGrid>
      </Modal>

      <ConfirmDialog
        open={confirmGroupDelete !== null}
        title="Delete merge field group"
        message={
          <span>
            Soft-delete group{' '}
            <code>{String(confirmGroupDelete?.groupCode ?? '')}</code>?
            Definitions in the group are not deleted; they keep their group
            reference.
          </span>
        }
        busy={groupBusy}
        onConfirm={() => void onDeleteGroup()}
        onCancel={() => setConfirmGroupDelete(null)}
      />
      <ConfirmDialog
        open={confirmDefDelete !== null}
        title="Delete merge field definition"
        message={
          <span>
            Soft-delete definition{' '}
            <code>{String(confirmDefDelete?.fieldCode ?? '')}</code>? Templates
            using this token will surface "required miss" warnings on next
            render.
          </span>
        }
        busy={defBusy}
        onConfirm={() => void onDeleteDef()}
        onCancel={() => setConfirmDefDelete(null)}
      />
    </>
  );
}
